import {
  AnnouncementsResponse,
  NotificationData,
  SystemMessageResponse,
  UploadFilesResponse,
  Version,
  UserSocialInfo,
  ReferrerReviewResponse,
} from '@/models/system'
import { apiClient } from './http'
import { StorageKey } from '@/models/enums'
const { protocol, host, port, clientId, AERIAL_PREFIX } = apiConfig
const ApiServerHost = IS_DEV ? `/api` : `${protocol}://${host}:${port}${AERIAL_PREFIX}`
const module = 'system'

export const querySystemMessage = async (): Promise<SystemMessageResponse[]> => {
  const endpoint = `${module}/message/app/list`
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'get',
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

export const querySystemMessageList = async (
  params: RecordParams,
  signal?: AbortController['signal'],
): Promise<RecordData<SystemMessageResponse, SystemMessageResponse[]>> => {
  const endpoint = `${module}/message/list`
  try {
    const res = await apiClientV2({
      url: endpoint,
      method: 'get',
      params,
      signal,
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

export const querySystemMessageRead = async (id: string): Promise<any> => {
  const endpoint = `${module}/message/app/${id}`
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'get',
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

export const querySystemMessageCount = async (): Promise<any> => {
  const endpoint = `${module}/message/app/count`
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'get',
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description 查詢MSS版本
 * @returns
 */
export const queryServerVersion = async (): Promise<string> => {
  const endpoint = `${module}/config/getVersion`
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'get',
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description 查詢系统公告
 * @returns
 */
export const queryServerAnnouncements = async (): Promise<AnnouncementsResponse[]> => {
  const endpoint = '/system/announcement'
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'get',
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description 查詢用戶系統通知
 * @returns
 */
export const queryServerNotification = async (): Promise<NotificationData[]> => {
  const endpoint = '/system/notification/query'
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'get',
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description 標記用戶系統通知
 * @param notification_id 系統通知id
 * @param from 查詢通知開始日期, 沒有表示全部 2024-09-09
 * @returns
 */
export const queryServerNotificationRead = async (notifications: string[]): Promise<any> => {
  const endpoint = '/system/notification/read'
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'post',
      data: {
        notifications,
        timestamp: new Date().getTime(),
      },
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description 上传文件
 * @param {any} files 上传文件对象
 * @returns {Promise<HttpResponse>} 上传结果
 */
export const uploadFiles = async (files: any): Promise<any> => {
  const usertokens = storage.getItem(StorageKey.USER_TOKENS)
  if (!usertokens) {
    return Promise.reject({
      code: 'ERR_UNKNOWN',
      message: 'no token',
      data: '',
      timestamp: new Date().toISOString(),
    })
  }
  try {
    const res = await fetch(ApiServerHost + '/upload/files', {
      method: 'post',
      headers: {
        Authorization: 'Bearer ' + usertokens.access_token,
      },
      body: files,
    })
    const data = await res.json()
    return await Promise.resolve(data)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description V2 上传文件
 * @param {any} files 上传文件对象
 * @returns {Promise<HttpResponse>} 上传结果
 */
export const uploadFilesV2 = async (files: any): Promise<any> => {
  const userStore = useUserStore()
  const usertokens = storage.getItem(StorageKey.USER_TOKENS) || userStore.userInfo.userTokens
  if (!usertokens || !usertokens.access_token) {
    return Promise.reject({
      code: 'ERR_UNKNOWN',
      message: 'no token',
      data: '',
      timestamp: new Date().toISOString(),
    })
  }
  try {
    const res = await fetch(ApiServerHost + '/resource/oss/app/uploads', {
      method: 'post',
      headers: {
        Authorization: 'Bearer ' + usertokens.access_token,
        clientid: clientId,
      },
      body: files,
    })
    const data = await res.json()
    return await Promise.resolve(data)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description 通过用户 ID 获取用户社会关系化信息
 * @param userId 用户社会关系化 ID
 * @returns 用户社会关系化信息
 */
export const queryUserSocialInfoById = async (userId: string): Promise<UserSocialInfo> => {
  const endpoint = `/system/social/qrCode/${userId}`
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'get',
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description 查询当前用户推荐官审核状态
 */
export const queryReferrerReviewStatus = async (): Promise<ReferrerReviewResponse> => {
  const endpoint = '/system/recommend/audit/getApplyStatus'
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'get',
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * @description 当前用户申请推荐官
 */
export const referrerApply = async (): Promise<ReferrerReviewResponse> => {
  const endpoint = '/system/recommend/audit/applyForReferrer'
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'put',
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}

/**
 * 获取航拍圈全部动态状态
 * true 上线
 * false 下线
 */
export const getAppShowState = async (): Promise<boolean> => {
  const endpoint = '/mssom/dynamic/appShowState'
  try {
    const res = await apiClient({
      url: endpoint,
      method: 'get',
    })
    return await Promise.resolve(res)
  } catch (error) {
    return await Promise.reject(error)
  }
}
